import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, Trophy, Radio } from "lucide-react";
import { API } from "@/hooks/useGiveawayStats";
import { TICKETS } from "@/data/giveaway";

const accentText = { gold: "text-gold", ember: "text-ember", blood: "text-blood", solana: "text-solana" };
const tierAccent = (tier) => accentText[TICKETS.find((t) => t.tier === tier)?.accent] || "text-gold";

const relTime = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

export const LiveFeed = () => {
  const [buyers, setBuyers] = useState([]);
  const [winners, setWinners] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/giveaway/activity?limit=12`);
      setBuyers(data.buyers || []);
      setWinners(data.winners || []);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 9000);
    return () => clearInterval(id);
  }, [load]);

  return (
    <section className="relative py-16 md:py-20 overflow-hidden" data-testid="live-feed-section">
      <div className="relative z-10 max-w-4xl mx-auto px-5 md:px-8">
        <div className="flex items-center justify-center gap-2 mb-8">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
          </span>
          <span className="inline-flex items-center gap-2 font-body text-sm font-bold tracking-widest uppercase text-white">
            <Radio className="w-4 h-4 text-ember" /> Live Activity
          </span>
        </div>

        {winners.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 glass rounded-lg p-5 border-gold/30"
            data-testid="live-winners"
          >
            <p className="flex items-center gap-2 font-body text-xs font-bold uppercase tracking-widest text-gold mb-3">
              <Trophy className="w-4 h-4" /> Latest Winners
            </p>
            <div className="grid sm:grid-cols-3 gap-3">
              {winners.map((w, i) => (
                <div key={i} className="rounded-md bg-black/40 border border-white/10 p-3">
                  <p className="font-body text-[11px] uppercase tracking-wide text-gray-400">{w.prize}</p>
                  <p className="font-body text-white font-bold text-sm mt-0.5 break-all">{w.name}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        <div className="glass rounded-lg divide-y divide-white/5 overflow-hidden" data-testid="live-buyers">
          {buyers.length === 0 ? (
            <div className="p-8 text-center">
              <Flame className="w-7 h-7 text-ember/60 mx-auto mb-3" />
              <p className="font-body text-gray-400">Be the first to join the stampede. Grab a ticket and light the fuse 🔥</p>
            </div>
          ) : (
            <AnimatePresence initial={false}>
              {buyers.map((b, i) => (
                <motion.div
                  key={`${b.name}-${b.created_at}`}
                  layout
                  initial={{ opacity: 0, x: -20, backgroundColor: "rgba(255,69,0,0.12)" }}
                  animate={{ opacity: 1, x: 0, backgroundColor: "rgba(0,0,0,0)" }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="flex items-center gap-3 px-5 py-3.5"
                  data-testid={`live-buyer-${i}`}
                >
                  <div className="w-8 h-8 rounded-full bg-gold/10 border border-gold/20 flex items-center justify-center shrink-0">
                    <Flame className={`w-4 h-4 ${tierAccent(b.tier)}`} />
                  </div>
                  <p className="font-body text-sm text-gray-200 flex-1 min-w-0 truncate">
                    <span className="font-semibold text-white">{b.name}</span> grabbed a{" "}
                    <span className={`font-bold ${tierAccent(b.tier)}`}>{b.ticket_name}</span> ticket
                    <span className="text-gray-500"> · {b.entries}x entries</span>
                  </p>
                  <span className="font-body text-xs text-gray-500 shrink-0">{relTime(b.created_at)}</span>
                </motion.div>
              ))}
            </AnimatePresence>
          )}
        </div>
        <p className="mt-3 text-center font-body text-xs text-gray-600">Updates live · identities partially hidden for privacy</p>
      </div>
    </section>
  );
};
